import { addFlowStatusFromCause } from '#~/agent/components/bridge/add-flow-diagnostic.js';
import { TeardownWait, Timeout } from './constants.js';
import type { Discover } from './types.js';
import { broker } from '#~/agent/index.js';
import { awaitPendingTeardowns } from '#~/device/commands/teardown.js';
import { logger } from '#~/logger/index.js';
import { classifyDiscoveryCause, type DiscoveryCommandResult } from '../discovery-outcome.js';

/**
 * Performs device discovery.
 *
 * Accepted means the mutate was delivered, not that the scan is complete.
 * NATS timeouts are transient outcomes, not success-shaped voids.
 */
export const discover: Discover = async (agentId, options): Promise<DiscoveryCommandResult> => {
  try {
    const proxy = broker.acquire(agentId);

    if (!proxy) {
      logger.debug('device discovery skipped: selected Bridge is not ready', { agentId });
      return classifyDiscoveryCause(new Error('DV-681'));
    }

    /*
     * ENG-4477: an immediate re-discovery right after a source delete used to
     * race the torn-down device — the discovery mutation stacked behind the
     * still-in-flight removal, timed out, and stormed "Unable to perform
     * device discovery" toasts while state sync resynced. Wait for the
     * agent's pending teardown to settle first (intelligent wait, capped).
     */
    await awaitPendingTeardowns(agentId, TeardownWait);

    await proxy.mutate(
      {
        command: {
          discovery: options?.timeout ?? Timeout,
          // Tells the agent whether to run its ONVIF name-match this cycle.
          extendedDiscovery: options?.extendedDiscovery ?? false
        }
      },
      {}
    );

    /*
     * Discovery re-arms every 15 s (constants.ts Timeout), so a per-scan success
     * record would mint a reference and a duration on every kick for the life of
     * the dialog. Only failures are recorded, and only categorically.
     * Per rule: stay-in-scope (ai.knowledge/global).
     */
    return { kind: 'accepted' };
  } catch (cause) {
    const result = classifyDiscoveryCause(cause);
    // Categorical fields only — no raw cause text, message or source URL.
    // See knowledge: use-canonical-logger-not-console (ai.knowledge/studio.client).
    const record = {
      agentId,
      outcome: result.kind,
      ...(result.kind === 'rejected' ? { reason: result.reason } : {}),
      status: addFlowStatusFromCause(cause)
    };

    if (result.kind === 'transient') {
      // Automatic retry continues; the selector presents the typed failure without a toast.
      logger.debug('device discovery hit a transient transport failure', record);
    } else {
      logger.warning('device discovery failed', record);
    }

    return result;
  }
};
